import React, { useRef, useEffect, Suspense } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import './styles.scss'
import * as THREE from 'three'
import { Stats, useProgress, useDetectGPU } from "@react-three/drei"
import { createBrowserHistory } from "history"
import { Router, Link, Route, Routes, useLocation, useNavigate } from "react-router-dom"
import { CascadeText, HiddenText } from './helpers'
import { Gallery } from './routes/gallery'
import { About } from './routes/about'
import { Work } from './routes/work'
import { useStore } from './store'
import { Cube } from './cube'
import { EffectComposer, Selection, Select, SelectiveBloom, Noise } from '@react-three/postprocessing'

const history = createBrowserHistory({ window })

export function BrowserRouter({ children, history }) {
  let [state, dispatch] = React.useReducer((_, action) => action, {
    action: history.action,
    location: history.location
  })

  React.useLayoutEffect(() => history.listen(dispatch), [history])

  return (
    <Router
      children={children}
      action={state.action}
      location={state.location}
      navigator={history}
    />
  )
}

function Cursor(props) {
  const ref = document.getElementById('cursor')
  const dragging = useRef(useStore.getState().dragging)
  const hovering = useRef(useStore.getState().hovering)
  const hoverData = useRef(useStore.getState().hoverData)
  const drag = useStore.getState().drag
  const mouse = useThree(state => state.mouse)
  useEffect(() => useStore.subscribe(
    state => (dragging.current = state.dragging, hovering.current = state.hovering, hoverData.current = state.hoverData)
  ), [])
  let x = mouse.x, y = mouse.y, s = 100
  const onMouseDown = event => {
    drag(true)
  }
  const onMouseUp = event => {
    drag(false)
  }
  useEffect(() => {
    window.addEventListener('mousedown', onMouseDown)
    window.addEventListener('mouseup', onMouseUp)
    return () => {
      window.removeEventListener('mousedown', onMouseDown)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [])
  ref.style.transform = `translate3d(${x*50+50}vw, ${-y*50+50}vh, 0)`

  useFrame((state) => {
    x = THREE.MathUtils.lerp(x, hovering.current && hoverData.current.x >= 0 ? hoverData.current.x / window.innerWidth * 2 - 1 : state.mouse.x, 0.1)
    y = THREE.MathUtils.lerp(y, hovering.current && hoverData.current.y >= 0 ? 1 - hoverData.current.y / window.innerHeight * 2 : state.mouse.y, 0.1)
    if(ref) {
      if(dragging.current) s = THREE.MathUtils.lerp(s, 40, 0.1)
      else if(hovering.current) s = THREE.MathUtils.lerp(s, hoverData.current.s, 0.1)
      else s = THREE.MathUtils.lerp(s, 80, 0.1)

      ref.style.transform = `translate3d(${x*50+50}vw, ${-y*50+50}vh, 0)`
      ref.style.width = ref.style.height = `${s}px`
      ref.style.left = ref.style.top = `-${s/2}px`
      if(x !== 0 && y !== 0) ref.style.opacity = '1'
    }
  })
}

function DelayedLink({ to, children, ...props }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { setTransition, transition } = useStore()

  const startTransition = (e) => {
    e.preventDefault()
    if(transition || location.pathname === to) return
    setTransition(true);
    [...document.getElementsByClassName('hidden-text-container')].forEach(el => {
      el.classList.add('fadeout')
    });
    [...document.querySelectorAll('.cascade-text-container.fadeout-enabled')].forEach(el => {
      el.classList.add('fadeout')
    })
    const root = document.getElementById('root')
    if(to === '/about') root.classList.add('light')
    else root.classList.remove('light')
    setTimeout(() => {
      navigate(to)
    }, 2000)
  }

  return (
    <Link {...props} to={to} onClick={startTransition}>
      {children}
    </Link>
  )
}

function Overlay() {
  const location = useLocation()
  const delay = location.pathname === '/' ? 2 : 0.5
  return (
    <nav>
      <DelayedLink to="/gallery" style={{ position: 'absolute', bottom: '1em', left: '1em', pointerEvents: 'all', zIndex: 1 }}>
        <CascadeText delay={delay} hidden invert="true">GALLERY</CascadeText>
      </DelayedLink>
      <DelayedLink to="/about" style={{ position: 'absolute', top: '1em', right: '1em', pointerEvents: 'all', zIndex: 1 }}>
        <CascadeText delay={delay} hidden>ABOUT</CascadeText>
      </DelayedLink>
      <DelayedLink to="/" style={{ position: 'absolute', top: '1em', left: '1em', pointerEvents: 'all', zIndex: 1 }}>
        <CascadeText delay={delay} hidden>WORK</CascadeText>
      </DelayedLink>
    </nav>
  )
}

function RouterContent() {
  const location = useLocation()
  const state = useThree()
  const setTransition = useStore.getState().setTransition
  const loaded = useRef(useStore.getState().loaded)
  useEffect(() => useStore.subscribe(
    state => (loaded.current = state.loaded)
  ), [])
  useEffect(
    () => {
      state.camera.position.set(0, 0, 5)
      state.camera.lookAt(0, 0, 0)
      const root = document.getElementById('root')
      if(location.pathname === '/about') root.classList.add('light')
      if(loaded.current) setTransition(false);
      [...document.getElementsByClassName('cascade-text-container')].forEach(el => {
        el.classList.remove('fadeout')
      })
      if(location.pathname === '/gallery') document.getElementsByTagName('canvas')[0].style.zIndex = 0
      else document.getElementsByTagName('canvas')[0].style.zIndex = 1
    },
    [location]
  )
  return (
    <Routes>
      <Route path="/" element={<Work />} />
      <Route path="/about" element={<About/>} />
      <Route path="/gallery" element={<Gallery />} />
    </Routes>
  )
}

function LoaderOverlay(props) {
  const { loaded } = useProgress()
  const setTransition = useStore.getState().setTransition
  const setLoaded = useStore.getState().setLoaded
  const ref = useRef()
  if(loaded >= 26) {
    setTimeout(() => { 
      ref?.current?.classList.add('fadeout')
      setTimeout(() => {
        ref?.current?.classList.add('hidden')
        setTimeout(() => {
          setLoaded(true) 
          setTransition(false)
        }, 1000)
      }, 1000)
    }, 2000)
  }
  return (
    <div className="loader" ref={ref}>
      <HiddenText show single vertical noload>
        {`${Math.floor(loaded * 100 / 26)}`}
      </HiddenText>
    </div>
  )
}

function Scene(props) {
  const ref = useRef()
  // preload gallery content
  const { loaded, total } = useProgress()
  const gpuTier = useDetectGPU()
  return (
    <Canvas flat dpr={[1, gpuTier.fps >= 60 ? 2 : 1]}>
      <Suspense fallback={null}>
        <Selection>
          <ambientLight intensity={1} ref={ref}/>
          <Stats showPanel={false}/>
          <BrowserRouter history={history}>
              <RouterContent />
              <Select enabled>
                <Cube />
              </Select>
          </BrowserRouter>
          <EffectComposer>
              <SelectiveBloom lights={[ref]} intensity={0.5} height={300} luminanceThreshold={0} luminanceSmoothing={0.9}/>
              <Noise opacity={0.05} />
            </EffectComposer>
          {window.innerWidth > 768 ? <Cursor /> : <></>}
        </Selection>
        {loaded < total ? <>
          <Gallery />
          <Work />
        </> : <></>}
      </Suspense>
  </Canvas>
  )
}

export default function App() {
  return (
    <>
      <BrowserRouter history={history}>
        <Overlay />
        <Scene />
        <div className="cursor" id="cursor" />
      </BrowserRouter>
      <LoaderOverlay />
    </>
  )
}